import React, { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Fade,
  Tooltip,
  Avatar,
  Divider,
} from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Person from '@mui/icons-material/Person';
import AccessTime from '@mui/icons-material/AccessTime';
import TrendingUp from '@mui/icons-material/TrendingUp';
import FileDownload from '@mui/icons-material/FileDownload';
import Refresh from '@mui/icons-material/Refresh';

// Đổi số giờ thập phân (vd 2.583) → "2 giờ 35 phút" cho dễ đọc
const formatHM = (hours) => {
  const totalMinutes = Math.round((Number(hours) || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0 && m === 0) return '0 phút';
  if (m === 0) return `${h} giờ`;
  if (h === 0) return `${m} phút`;
  return `${h} giờ ${m} phút`;
};

// Modern Theme
const modernTheme = {
  colors: {
    primary: '#2563eb',
    secondary: '#059669',
    accent: '#7c3aed',
    warning: '#ea580c',
    error: '#dc2626',
    success: '#16a34a',
    info: '#0284c7',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    secondary: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
    accent: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
  }
};

const LivestreamTable = ({
  livestreams = [],
  users = [],
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  handleEdit,
  handleDelete,
  isMobile
}) => {
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Quick filter handler
  const handleQuickFilter = (value) => {
    setQuickFilter(value);
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    switch (value) {
      case 'today':
        setStartDate(formatDate(today));
        setEndDate(formatDate(today));
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setStartDate(formatDate(yesterday));
        setEndDate(formatDate(yesterday));
        break;
      case '7days':
        const week = new Date(today);
        week.setDate(week.getDate() - 7);
        setStartDate(formatDate(week));
        setEndDate(formatDate(today));
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDate(formatDate(firstDay));
        setEndDate(formatDate(today));
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        setStartDate(formatDate(lastMonthStart));
        setEndDate(formatDate(lastMonthEnd));
        break;
      case 'thisYear':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        setStartDate(formatDate(yearStart));
        setEndDate(formatDate(today));
        break;
      case 'all':
      default:
        setStartDate('');
        setEndDate('');
        break;
    }
  };

  // Tạo một map userId -> user name để tìm kiếm hiệu quả
  const userMap = useMemo(() => {
    const map = {};
    users.forEach(user => {
      map[user._id] = user.fullName || user.username || 'N/A';
    });
    return map;
  }, [users]);

  // Hàm lấy tên người dùng từ userId
  const getUserName = (userId) => {
    // Kiểm tra nếu userId là object (đã được populate)
    if (typeof userId === 'object' && userId?._id) {
      return userId.fullName || userId.username || 'N/A';
    }
    // Tìm trong userMap
    return userMap[userId] || 'N/A';
  };

  // Filter livestreams
  const filteredLivestreams = useMemo(() => {
    let filtered = [...livestreams];

    // Search by name
    if (searchText) {
      filtered = filtered.filter(ls => 
        getUserName(ls.userId).toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filter by user
    if (selectedUser !== 'all') {
      filtered = filtered.filter(ls => {
        const userId = typeof ls.userId === 'object' ? ls.userId._id : ls.userId;
        return userId === selectedUser;
      });
    }

    // Filter by date range
    if (startDate) {
      filtered = filtered.filter(ls => new Date(ls.date) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter(ls => new Date(ls.date) <= new Date(endDate));
    }

    return filtered;
  }, [livestreams, searchText, selectedUser, startDate, endDate, getUserName]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = filteredLivestreams.length;
    const totalHours = filteredLivestreams.reduce((sum, ls) => sum + (ls.hours || 0), 0);
    const uniqueUsers = new Set(filteredLivestreams.map(ls => 
      typeof ls.userId === 'object' ? ls.userId._id : ls.userId
    )).size;

    return { total, totalHours, uniqueUsers };
  }, [filteredLivestreams]);

  // Reset filters
  const handleResetFilters = () => {
    setSearchText('');
    setSelectedUser('all');
    setStartDate('');
    setEndDate('');
    setQuickFilter('all');
    setPage(0);
  };

  // Export to CSV
  const handleExport = () => {
    const csvContent = [
      ['Nhân viên', 'Ngày', 'Số giờ'],
      ...filteredLivestreams.map(ls => [
        getUserName(ls.userId),
        new Date(ls.date).toLocaleDateString('vi-VN'),
        ls.hours
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `livestream-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleChangePage = (event, newPage) => {
    if (Array.isArray(filteredLivestreams)) {
      const maxPage = Math.ceil(filteredLivestreams.length / rowsPerPage) - 1;
      setPage(Math.min(newPage, maxPage >= 0 ? maxPage : 0));
    } else {
      setPage(0);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Fade in timeout={600}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card 
              sx={{ 
                background: modernTheme.gradients.primary,
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(37, 99, 235, 0.3)'
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <CalendarMonth sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Tổng buổi live
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {stats.total}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card 
              sx={{ 
                background: modernTheme.gradients.secondary,
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(5, 150, 105, 0.3)'
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <AccessTime sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Tổng giờ live
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {formatHM(stats.totalHours)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card 
              sx={{ 
                background: modernTheme.gradients.accent,
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(124, 58, 237, 0.3)'
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <Person sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Nhân viên
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {stats.uniqueUsers}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Main Table Card */}
      <Fade in timeout={800}>
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: 3,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderBottom: '1px solid #e5e7eb'
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: modernTheme.colors.primary }}>
                  📋 Danh sách chấm công Livestream
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  Quản lý và theo dõi lịch sử livestream
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title="Làm mới">
                  <IconButton 
                    onClick={() => window.location.reload()}
                    sx={{ 
                      bgcolor: 'white',
                      border: '1px solid #e5e7eb',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Xuất Excel">
                  <IconButton 
                    onClick={handleExport}
                    sx={{ 
                      bgcolor: 'white',
                      border: '1px solid #e5e7eb',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                  >
                    <FileDownload />
                  </IconButton>
                </Tooltip>
                <Button
                  variant={showFilters ? "contained" : "outlined"}
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    ...(showFilters && {
                      background: modernTheme.gradients.primary
                    })
                  }}
                >
                  Bộ lọc
                </Button>
              </Stack>
            </Stack>

            {/* Filters */}
            {showFilters && (
              <Fade in timeout={400}>
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 3 }} />
                  
                  {/* Quick Filters */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>
                      ⚡ Bộ lọc nhanh
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Khoảng thời gian</InputLabel>
                          <Select
                            value={quickFilter}
                            label="Khoảng thời gian"
                            onChange={(e) => handleQuickFilter(e.target.value)}
                            sx={{
                              bgcolor: 'white',
                              borderRadius: 2,
                              fontWeight: 600,
                              '& .MuiSelect-select': {
                                color: quickFilter !== 'all' ? modernTheme.colors.primary : 'inherit'
                              }
                            }}
                          >
                            <MenuItem value="all">📅 Tất cả</MenuItem>
                            <MenuItem value="today">🔵 Hôm nay</MenuItem>
                            <MenuItem value="yesterday">⚪ Hôm qua</MenuItem>
                            <MenuItem value="7days">📊 7 ngày qua</MenuItem>
                            <MenuItem value="thisMonth">📆 Tháng này</MenuItem>
                            <MenuItem value="lastMonth">📅 Tháng trước</MenuItem>
                            <MenuItem value="thisYear">🗓️ Năm nay</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {quickFilter !== 'all' && (
                        <Grid item xs={12} sm={6} md={9}>
                          <Box 
                            sx={{ 
                              p: 1.5,
                              bgcolor: `${modernTheme.colors.primary}10`,
                              borderRadius: 2,
                              border: `1px solid ${modernTheme.colors.primary}30`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <CalendarMonth sx={{ color: modernTheme.colors.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: modernTheme.colors.primary }}>
                              {startDate && endDate ? (
                                `${new Date(startDate).toLocaleDateString('vi-VN')} → ${new Date(endDate).toLocaleDateString('vi-VN')}`
                              ) : (
                                'Đang lọc...'
                              )}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Advanced Filters */}
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>
                    🔍 Bộ lọc nâng cao
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Tìm theo tên..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: modernTheme.colors.primary }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Nhân viên</InputLabel>
                        <Select
                          value={selectedUser}
                          label="Nhân viên"
                          onChange={(e) => setSelectedUser(e.target.value)}
                          sx={{
                            bgcolor: 'white',
                            borderRadius: 2,
                          }}
                        >
                          <MenuItem value="all">Tất cả</MenuItem>
                          {users.map(user => (
                            <MenuItem key={user._id} value={user._id}>
                              {user.fullName || user.username}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Từ ngày"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setQuickFilter('all'); // Reset quick filter khi chỉnh manual
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Đến ngày"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setQuickFilter('all'); // Reset quick filter khi chỉnh manual
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12} md={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleResetFilters}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          height: '40px'
                        }}
                      >
                        Đặt lại
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Active Filters */}
                  {(searchText || selectedUser !== 'all' || quickFilter !== 'all' || startDate || endDate) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap">
                      <Typography variant="body2" sx={{ color: '#64748b', mr: 1, alignSelf: 'center', fontWeight: 600 }}>
                        🏷️ Đang lọc:
                      </Typography>
                      {searchText && (
                        <Chip 
                          label={`Tìm: ${searchText}`} 
                          size="small" 
                          onDelete={() => setSearchText('')}
                          sx={{ 
                            mb: 1,
                            bgcolor: `${modernTheme.colors.info}15`,
                            color: modernTheme.colors.info,
                            fontWeight: 600,
                            border: `1px solid ${modernTheme.colors.info}40`
                          }}
                        />
                      )}
                      {selectedUser !== 'all' && (
                        <Chip 
                          label={`NV: ${userMap[selectedUser]}`}
                          size="small" 
                          onDelete={() => setSelectedUser('all')}
                          sx={{ 
                            mb: 1,
                            bgcolor: `${modernTheme.colors.accent}15`,
                            color: modernTheme.colors.accent,
                            fontWeight: 600,
                            border: `1px solid ${modernTheme.colors.accent}40`
                          }}
                        />
                      )}
                      {quickFilter !== 'all' && (
                        <Chip 
                          label={(() => {
                            const labels = {
                              today: '🔵 Hôm nay',
                              yesterday: '⚪ Hôm qua',
                              '7days': '📊 7 ngày qua',
                              thisMonth: '📆 Tháng này',
                              lastMonth: '📅 Tháng trước',
                              thisYear: '🗓️ Năm nay'
                            };
                            return labels[quickFilter] || quickFilter;
                          })()}
                          size="small" 
                          onDelete={() => handleQuickFilter('all')}
                          sx={{ 
                            mb: 1,
                            bgcolor: `${modernTheme.colors.primary}15`,
                            color: modernTheme.colors.primary,
                            fontWeight: 600,
                            border: `1px solid ${modernTheme.colors.primary}40`
                          }}
                        />
                      )}
                      {quickFilter === 'all' && startDate && (
                        <Chip 
                          label={`Từ: ${new Date(startDate).toLocaleDateString('vi-VN')}`}
                          size="small" 
                          onDelete={() => setStartDate('')}
                          sx={{ 
                            mb: 1,
                            bgcolor: `${modernTheme.colors.success}15`,
                            color: modernTheme.colors.success,
                            fontWeight: 600,
                            border: `1px solid ${modernTheme.colors.success}40`
                          }}
                        />
                      )}
                      {quickFilter === 'all' && endDate && (
                        <Chip 
                          label={`Đến: ${new Date(endDate).toLocaleDateString('vi-VN')}`}
                          size="small" 
                          onDelete={() => setEndDate('')}
                          sx={{ 
                            mb: 1,
                            bgcolor: `${modernTheme.colors.success}15`,
                            color: modernTheme.colors.success,
                            fontWeight: 600,
                            border: `1px solid ${modernTheme.colors.success}40`
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Box>
              </Fade>
            )}
          </Box>

          {/* Table */}
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, color: modernTheme.colors.primary }}>
                    Nhân viên
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: modernTheme.colors.primary }}>
                    Ngày
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: modernTheme.colors.primary }}>
                    Số giờ
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: modernTheme.colors.primary }}>
                    Thao tác
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLivestreams.length > 0 ? (
                  filteredLivestreams
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((livestream, index) => (
                      <TableRow 
                        key={livestream._id}
                        sx={{
                          '&:hover': { bgcolor: '#f8fafc' },
                          transition: 'background-color 0.2s',
                          animation: `fadeIn 0.3s ease-in-out ${index * 0.05}s both`
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar 
                              sx={{ 
                                width: 36, 
                                height: 36,
                                bgcolor: modernTheme.colors.primary,
                                fontSize: '0.9rem',
                                fontWeight: 600
                              }}
                            >
                              {getUserName(livestream.userId).charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {getUserName(livestream.userId)}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                            label={new Date(livestream.date).toLocaleDateString('vi-VN')}
                            size="small"
                            sx={{ 
                              bgcolor: '#f0f9ff',
                              color: modernTheme.colors.info,
                              fontWeight: 600,
                              border: `1px solid ${modernTheme.colors.info}40`
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<AccessTime sx={{ fontSize: 16 }} />}
                            label={formatHM(livestream.hours)}
                            size="small"
                            sx={{ 
                              bgcolor: '#f0fdf4',
                              color: modernTheme.colors.success,
                              fontWeight: 600,
                              border: `1px solid ${modernTheme.colors.success}40`
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Chỉnh sửa">
                              <IconButton 
                                onClick={() => handleEdit(livestream)} 
                                size="small"
                                sx={{
                                  color: modernTheme.colors.primary,
                                  bgcolor: `${modernTheme.colors.primary}10`,
                                  '&:hover': { 
                                    bgcolor: `${modernTheme.colors.primary}20`,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Xóa">
                              <IconButton 
                                onClick={() => handleDelete(livestream._id)} 
                                size="small"
                                sx={{
                                  color: modernTheme.colors.error,
                                  bgcolor: `${modernTheme.colors.error}10`,
                                  '&:hover': { 
                                    bgcolor: `${modernTheme.colors.error}20`,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <TrendingUp sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                          Không có dữ liệu
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          {searchText || selectedUser !== 'all' || startDate || endDate 
                            ? 'Thử điều chỉnh bộ lọc để xem kết quả khác'
                            : 'Chưa có lịch sử livestream nào'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredLivestreams.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? "Dòng:" : "Số hàng mỗi trang:"}
            rowsPerPageOptions={isMobile ? [5, 10, 25] : [10, 25, 50]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              bgcolor: '#f8fafc'
            }}
          />
        </Paper>
      </Fade>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default LivestreamTable; 